import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PanelContent from '@shared/components/PanelContent';
import UnsavedChangesDialog from '@shared/components/UnsavedChangesDialog';
import { useUnsavedChangesGuard } from '@shared/hooks/useUnsavedChangesGuard';
import { useMapStore } from '@shared/hooks/useMapStore';
import { useRoute } from './hooks/useRoute';
import { useRouteEdit } from './hooks/useRouteEdit';
import { PATHS } from '@app/routes/paths';
import RouteNodeList from './components/RouteNodeList';

export default function RouteEditPage() {
  const { routeId } = useParams<{ routeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const { data: route, isLoading } = useRoute(routeId!);
  const routeEdit = useRouteEdit();
  const setSelectedRouteId = useMapStore((s) => s.setSelectedRouteId);
  const setActiveProductsRouteId = useMapStore((s) => s.setActiveProductsRouteId);

  // Enter edit mode when route data arrives
  useEffect(() => {
    if (route && !routeEdit.isEditing) {
      routeEdit.startEdit(route.routeId, route.name, route.waypoints);
      setSelectedRouteId(route.routeId);
    }
  }, [route]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep a ref to the latest cancelEdit so the unmount cleanup always calls the current version
  const cancelEditRef = useRef(routeEdit.cancelEdit);
  useEffect(() => {
    cancelEditRef.current = routeEdit.cancelEdit;
  });

  // Cleanup on unmount: exit edit mode
  useEffect(() => {
    return () => {
      cancelEditRef.current();
    };
  }, []);

  const { showDialog, handleSave, handleDiscard, handleCancel } = useUnsavedChangesGuard({
    hasUnsavedChanges: routeEdit.isDirty,
    onSave: () => routeEdit.saveEdit(),
    onDiscard: () => routeEdit.cancelEdit(),
  });

  function navigateBack() {
    if (returnTo === 'details' && routeId) {
      setActiveProductsRouteId(routeId);
      navigate(`/routes/${routeId}/details`);
    } else {
      navigate(PATHS.ROUTES);
    }
  }

  function handleBack() {
    navigateBack();
  }

  async function handleSaveClick() {
    await routeEdit.saveEdit();
    navigateBack();
  }

  function handleCancelClick() {
    routeEdit.revertEdit();
    navigateBack();
  }

  if (isLoading || !route) {
    return (
      <PanelContent>
        <Typography>Loading...</Typography>
      </PanelContent>
    );
  }

  return (
    <div data-testid="page-route-edit" className="flex flex-col h-full">
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton size="small" onClick={handleBack} aria-label="Back to routes">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Edit Route
        </Typography>
      </Box>

      {/* Route name */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <TextField
          label="Route Name"
          value={routeEdit.dirtyName}
          onChange={(e) => routeEdit.setName(e.target.value)}
          size="small"
          fullWidth
          data-testid="route-name-input"
        />
      </Box>

      {/* Node list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <RouteNodeList waypoints={routeEdit.dirtyWaypoints} />
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<CancelIcon />}
          onClick={handleCancelClick}
          disabled={routeEdit.isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSaveClick}
          disabled={routeEdit.isSaving || !routeEdit.isDirty}
        >
          {routeEdit.isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <UnsavedChangesDialog
        open={showDialog}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onCancel={handleCancel}
      />
    </div>
  );
}
